import { trackEvent } from "@intercom/messenger-js-sdk";
import { AmplifilesClient } from "./api-client";
import {
  AnalyticsEventData,
  AnalyticsEventRequestDto,
  EventName,
  EventUrl,
} from "@repo/api/analytics";
import type {
  DfyAddOns,
  DfyDeliveryDto,
  DfyOrderDetailsDto,
  DfyOrderDto,
  DfyOrderType,
} from "@repo/api/dfy";

export class AnalyticsApi {
  static async logEvent(
    eventType: EventUrl,
    eventData?: AnalyticsEventData,
    teamExternalId?: string,
  ): Promise<void> {
    const requestBody: AnalyticsEventRequestDto = {
      teamExternalId,
      eventData,
    };

    await AmplifilesClient.instance.post(`/events/${eventType}`, requestBody);
  }

  static async logUserLogout(teamExternalId?: string): Promise<void> {
    await AnalyticsApi.logEvent("logout", {}, teamExternalId);
  }

  static async logUserLogin(): Promise<void> {
    await AnalyticsApi.logEvent("login");
  }

  static async logUserSessionStart(teamExternalId?: string): Promise<void> {
    await AnalyticsApi.logEvent("session-start", {}, teamExternalId);
  }

  static async logTutorialClicked(
    teamExternalId?: string,
    tutorialId?: string,
  ): Promise<void> {
    await AnalyticsApi.logEvent(
      "tutorial-clicked",
      { tutorial_id: tutorialId },
      teamExternalId,
    );
  }

  static async logEditorOpened(teamExternalId?: string): Promise<void> {
    await AnalyticsApi.logEvent("editor-opened", {}, teamExternalId);
  }

  static async logCreditsPopupOpenedManual(
    initialTab: "SUBSCRIPTION" | "ONE_OFF",
    teamExternalId?: string,
  ): Promise<void> {
    const eventData = {
      initial_tab: initialTab,
    };

    trackEvent(EventName.EFS_CREDITS_POPUP_OPENED_MANUAL, eventData);
    await AnalyticsApi.logEvent(
      "credits-popup-manual",
      eventData,
      teamExternalId,
    );
  }

  static async logCreditsPopupOpenedInsufficient(
    requiredCredits: number,
    selectedImagesCount: number,
    initialTab: "SUBSCRIPTION" | "ONE_OFF",
    teamExternalId?: string,
  ): Promise<void> {
    const eventData = {
      required_credits: requiredCredits,
      selected_images_count: selectedImagesCount,
      initial_tab: initialTab,
    };

    trackEvent(EventName.EFS_CREDITS_POPUP_OPENED_INSUFFICIENT, eventData);

    await AnalyticsApi.logEvent(
      "credits-popup-insufficient",
      eventData,
      teamExternalId,
    );
  }

  static async logTeamSwitched(teamExternalId: string): Promise<void> {
    await AnalyticsApi.logEvent("team-switched", {}, teamExternalId);
  }

  static async logVideoCreated(): Promise<void> {
    trackEvent(EventName.EFS_VIDEO_CREATED);
  }

  static async logVideoDownloaded(): Promise<void> {
    trackEvent(EventName.EFS_VIDEO_DOWNLOADED);
  }

  static async logAnimateImageTriggered(): Promise<void> {
    trackEvent(EventName.EFS_ANIMATE_IMAGE_TRIGGERED);
  }

  static async logImageAdded(): Promise<void> {
    trackEvent(EventName.EFS_IMAGE_ADDED);
  }

  static async logVoiceOverAdded(): Promise<void> {
    trackEvent(EventName.EFS_VOICE_OVER_ADDED);
  }

  static async logListingScraped(): Promise<void> {
    trackEvent(EventName.EFS_LISTING_SCRAPED);
  }

  static async logRepeatingAnimationWarningPopupOpened(
    teamExternalId?: string,
  ): Promise<void> {
    await AnalyticsApi.logEvent(
      "repeating-animation-warning-popup",
      {},
      teamExternalId,
    );
  }

  static async logPartialAnimationPopupOpened(
    teamExternalId: string,
    selectedImagesCount: number,
    animatableImagesCount: number,
  ): Promise<void> {
    await AnalyticsApi.logEvent(
      "partial-animation-popup",
      {
        selected_images_count: selectedImagesCount,
        animatable_images_count: animatableImagesCount,
      },
      teamExternalId,
    );
  }

  static async logPartialAnimationConfirmed(
    teamExternalId: string,
    selectedImagesCount: number,
    animatableImagesCount: number,
  ): Promise<void> {
    await AnalyticsApi.logEvent(
      "partial-animation-confirmed",
      {
        selected_images_count: selectedImagesCount,
        animatable_images_count: animatableImagesCount,
      },
      teamExternalId,
    );
  }

  static async logAnimationsSuggested(): Promise<void> {
    trackEvent(EventName.EFS_SUGGEST_ANIMATIONS_TRIGGERED);
  }

  static async logReferralDialogOpened(
    source: string,
    teamExternalId?: string,
  ): Promise<void> {
    await AnalyticsApi.logEvent(
      "referral-dialog-opened",
      { source },
      teamExternalId,
    );
  }

  static async logReferralLinkCopied(teamExternalId?: string): Promise<void> {
    await AnalyticsApi.logEvent("referral-link-copied", {}, teamExternalId);
  }

  static async logAdvertiseVideoClicked(
    teamExternalId?: string,
  ): Promise<void> {
    await AnalyticsApi.logEvent("advertise-video-clicked", {}, teamExternalId);
  }

  static async logDfyLinkClicked(teamExternalId?: string): Promise<void> {
    await AnalyticsApi.logEvent("dfy-link-clicked", {}, teamExternalId);
  }

  static async logFeatureRequestsLinkClicked(
    teamExternalId?: string,
  ): Promise<void> {
    await AnalyticsApi.logEvent(
      "feature-requests-link-clicked",
      {},
      teamExternalId,
    );
  }

  static async logExportVideoDialogClosed(): Promise<void> {
    trackEvent(EventName.EFS_EXPORT_VIDEO_DIALOG_CLOSED);
  }

  static async logCreativeModeOpened(): Promise<void> {
    trackEvent(EventName.EFS_CREATIVE_MODE_OPENED);
  }

  static async logCustomImageEdit(): Promise<void> {
    trackEvent(EventName.EFS_CUSTOM_IMAGE_EDIT);
  }

  static async logCustomAnimate(): Promise<void> {
    trackEvent(EventName.EFS_CUSTOM_ANIMATE);
  }

  static async logBeatSyncTriggered(teamExternalId?: string): Promise<void> {
    trackEvent(EventName.EFS_BEAT_SYNC_TRIGGERED);
    await AnalyticsApi.logEvent("beat-sync-triggered", {}, teamExternalId);
  }

  static async logInspirationGuideOpened(
    guideId: string,
    teamExternalId?: string,
  ): Promise<void> {
    await AnalyticsApi.logEvent(
      "inspiration-guide-opened",
      { guide_id: guideId },
      teamExternalId,
    );
  }

  static async logImagePromptUsed(
    args: {
      guideId: string;
      promptId: string;
      autoNavigatedFromInstructions: boolean;
    },
    teamExternalId?: string,
  ): Promise<void> {
    await AnalyticsApi.logEvent(
      "image-prompt-used",
      {
        guide_id: args.guideId,
        prompt_id: args.promptId,
        auto_navigated_from_instructions: args.autoNavigatedFromInstructions,
      },
      teamExternalId,
    );
  }

  static async logAnimationPromptUsed(
    args: {
      guideId: string;
      promptId: string;
      autoNavigatedFromInstructions: boolean;
    },
    teamExternalId?: string,
  ): Promise<void> {
    await AnalyticsApi.logEvent(
      "animation-prompt-used",
      {
        guide_id: args.guideId,
        prompt_id: args.promptId,
        auto_navigated_from_instructions: args.autoNavigatedFromInstructions,
      },
      teamExternalId,
    );
  }

  static async logDfyOrderFormStepChanged(
    args: {
      orderExternalId: string;
      fromStep: string;
      toStep: string | null;
      direction: "FORWARD" | "BACKWARD";
      details: DfyOrderDetailsDto;
    },
    teamExternalId: string,
  ): Promise<void> {
    const { details } = args;
    await AnalyticsApi.logEvent(
      "dfy-order-form-step-changed",
      {
        dfy_order_external_id: args.orderExternalId,
        from_step: args.fromStep,
        to_step: args.toStep,
        direction: args.direction,
        is_last_step: args.toStep === null,
        package_type: details.type,
        add_ons: (Object.keys(details.addOns) as (keyof DfyAddOns)[]).filter(
          (key) => details.addOns[key],
        ),
        branding_templates: details.templates.map((template) => template.type),
        image_selection_mode: details.imageSelectionMode,
        selected_images_count: details.sourceImageConfigs.filter(
          (config) => config.isSelected,
        ).length,
      },
      teamExternalId,
    );
  }

  static async logDfyDeliveryDownloaded(
    args: { orderExternalId: string; delivery: DfyDeliveryDto },
    teamExternalId: string,
  ): Promise<void> {
    await AnalyticsApi.logEvent(
      "dfy-delivery-downloaded",
      {
        dfy_order_external_id: args.orderExternalId,
        delivery_external_id: args.delivery.externalId,
        aspect_ratio: args.delivery.videoRender.aspectRatio,
        upscale_quality: args.delivery.videoRender.upscaleQuality,
      },
      teamExternalId,
    );
  }

  static async logDfyExamplesDialogOpened(
    teamExternalId?: string,
  ): Promise<void> {
    await AnalyticsApi.logEvent(
      "dfy-examples-dialog-opened",
      {},
      teamExternalId,
    );
  }

  static async logDfyExamplePlayed(
    args: {
      exampleId: string;
      packageType: DfyOrderType;
      addOn?: keyof DfyAddOns;
      source: string;
    },
    teamExternalId?: string,
  ): Promise<void> {
    await AnalyticsApi.logEvent(
      "dfy-example-played",
      {
        example_id: args.exampleId,
        package_type: args.packageType,
        add_ons: args.addOn ? [args.addOn] : [],
        source: args.source,
      },
      teamExternalId,
    );
  }

  static async logDfyOrderOpened(
    args: { order: DfyOrderDto; deliveriesCount: number },
    teamExternalId: string,
  ): Promise<void> {
    await AnalyticsApi.logEvent(
      "dfy-order-opened",
      {
        dfy_order_external_id: args.order.externalId,
        order_status: args.order.status,
        package_type: args.order.details.type,
        deliveries_count: args.deliveriesCount,
      },
      teamExternalId,
    );
  }
}
